import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import Modal from '@mui/material/Modal'

import { usePreparation } from '@/hooks/usePreparation'
import { useTableData } from '@/hooks/useTableData'
import { inspectionBreakdownService } from '@/services/Inspection/InspectionBreakdownService'

import CustomButton from '@/components/CustomButton'
import CustomGraphy from '@/components/CustomGraphy'
import CustomView from '@/components/CustomView'
import FormInput from '@/components/FormInput'
import FormSelect from '@/components/FormSelect'
import Loader from '@/components/Loader'

import { newBreakdownSchema } from '@/utils/yup/schemas/preparation/new/PreparationNewSchema'
import { INews } from '@/types/customTable.types'
import { getIdByName } from '@/pages/Parametrization/utils'
import { fillSelectList, getCurrency, getIdListFiltered } from '@/utils/index'
import { CONSTANTS } from '@/utils/CONSTANTS'

function NewBreakdownPreparation({ returnToTable }: INews) {
  const {
    CATEGORY_OPTIONS,
    GET_BREAKDOWN,
    GET_BREAKDOWN_LIST,
    BREAKDOWN_LIST,
    BREAKDOWN_OPTIONS,
    PREPARATION_DATA,
    CATEGORY_LIST
  } = usePreparation()
  const { CHECK_SELECTS, SET_CHECK_SELECTS } = useTableData()
  const [inLoading, setLoading] = useState(false)
  const { inspection = {} } = PREPARATION_DATA
  const rowsSelecteds = getIdListFiltered(CHECK_SELECTS)
  const schema = newBreakdownSchema()

  const {
    values,
    handleSubmit,
    handleChange,
    errors,
    touched,
    setFieldValue,
    setValues
  } = useFormik({
    initialValues: {
      category: '',
      breakdown: '',
      value: 0,
      description: ''
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: (values) => {
      onSubmit(values)
    }
  })

  useEffect(() => {
    GET_BREAKDOWN_LIST({
      categoriaId: getIdByName(values.category, CATEGORY_LIST?.rows),
      ativo: true,
      pageSize: CONSTANTS.allRegisters
    })
  }, [values.category])

  async function onSubmit(values: any) {
    const payload = {
      avariaId: getIdByName(values.breakdown, BREAKDOWN_LIST?.rows),
      vistoriaId: inspection?.id,
      descricao: values.description
    }

    try {
      setLoading(true)
      const newBreakdown =
        await inspectionBreakdownService.postBreakdownInspection(payload)
      const breakdowns: any = await GET_BREAKDOWN(inspection?.id)
      const newListSelect = fillSelectList(breakdowns, [
        ...rowsSelecteds,
        newBreakdown.data.data.id
      ])
      toast.success('Avaria adicionada com sucesso.')
      SET_CHECK_SELECTS(newListSelect)
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
      returnToTable()
    }
  }

  const styleModal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200px',
    borderRadius: '8px',
    border: '1.5px solid #C4C4C4',
    boxShadow: 24,
    p: 4
  }

  const handleCategory = (name: string, value: string | null) => {
    if (!value) {
      setValues({ ...values, [name]: '', breakdown: '', value: 0 })
      return
    }

    setValues({ ...values, [name]: value, breakdown: '', value: 0 })
  }

  const handleBreakdown = (name: string, value: string | null) => {
    if (!value) {
      setValues({ ...values, [name]: '', value: 0 })
      return
    }

    const findBreakdown = BREAKDOWN_LIST?.rows?.find(
      (item: any) => item.nome === value
    )

    setValues({
      ...values,
      [name]: value,
      value: findBreakdown?.valor * 100,
      category: findBreakdown?.categoria
    })
  }

  return (
    <Modal keepMounted open={true}>
      <CustomView
        backColor="white"
        sx={styleModal}
        style={{ minWidth: '800px' }}
      >
        <CustomView
          backColor="white"
          justifyContent="center"
          p="32px"
          alignItems="center"
        >
          <CustomView borderBottom="1px solid #8F8E914D">
            <CustomGraphy fontSize="18px" mb="16px">
              Adicionar avaria
            </CustomGraphy>
          </CustomView>
          {inLoading ? (
            <Loader inLoading={inLoading} type={'component'} />
          ) : (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <CustomView
                maxWidth={967}
                mt="36px"
                justifyContent="flex-start"
                gap={1}
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <FormSelect
                  withBorder={true}
                  label="Categoria da avaria"
                  id="category"
                  name="category"
                  value={values.category}
                  onChange={handleCategory}
                  options={CATEGORY_OPTIONS}
                  errorMessage={touched.category ? errors?.category : ''}
                />
                <FormSelect
                  withBorder={true}
                  label="Avaria"
                  id="breakdown"
                  name="breakdown"
                  value={values.breakdown}
                  onChange={handleBreakdown}
                  options={BREAKDOWN_OPTIONS}
                  errorMessage={touched.breakdown ? errors?.breakdown : ''}
                />
                <CustomView>
                  <FormInput
                    disabled
                    mask="WithoutMask"
                    id="value"
                    name="value"
                    labelName="Valor estimado"
                    customStyle="withBorder"
                    value={getCurrency(values.value / 100)}
                  />
                </CustomView>
              </CustomView>
              <CustomView
                maxWidth={967}
                mt={{ xs: '2px', md: '15px' }}
                mb="40px"
                gap={1}
                justifyContent="flex-start"
                flexDirection={{ xs: 'column', md: 'row' }}
              >
                <FormInput
                  mask="WithoutMask"
                  id="description"
                  name="description"
                  labelName="Descrição"
                  customStyle="withBorder"
                  value={values.description}
                  onChange={handleChange}
                  errorMessage={touched.description ? errors?.description : ''}
                />
              </CustomView>
              <CustomView
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <CustomGraphy width="100%" as="lowOpacity">
                  * Campo de preenchimento obrigatório
                </CustomGraphy>
                <CustomView
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={8}
                >
                  <CustomButton variable="default" onClick={returnToTable}>
                    Cancelar
                  </CustomButton>
                  <CustomButton variable="bordered" type="submit">
                    Adicionar
                  </CustomButton>
                </CustomView>
              </CustomView>
            </form>
          )}
        </CustomView>
      </CustomView>
    </Modal>
  )
}

export default NewBreakdownPreparation
